/**
 * What she sounds like, and when.
 *
 * The scanner has had sound since the barcode lock: a two-tone chirp
 * synthesised on the spot, because a scanner needs to say "got it" in eighty
 * milliseconds and a downloaded file cannot promise that. She is the opposite
 * case. Her sounds are rare, they are allowed to take a moment to arrive, and
 * what they need to be is *hers* — a hum out of an appliance rather than a
 * beep out of a device. Oscillators are very bad at that, so these are files.
 *
 * Nine of them, kept deliberately small (mono AAC, ~25 KB each), fetched on
 * first use rather than bundled. Mood does not add files: a tone is detuned
 * and trimmed at playback, so November's version of a sound is the same asset
 * played slightly lower and slightly softer.
 *
 * iOS is the primary device and it will not make a sound until the page has
 * been touched, so every cue silently does nothing until then. That is not a
 * failure path — a chirp that queues up and fires late is worse than one that
 * never fires.
 */

import type { PresenceTone } from '@gramas/shared'

export type CueName =
  /** The day's first greeting, arriving with her reveal. */
  | 'greet'
  /** Her reply landing in the chat. The quietest voiced cue in the set. */
  | 'reply'
  /** A log she had something to say about — a notable one, not every meal. */
  | 'notable'
  /** A trophy. The only sound in the set that is allowed to be a moment. */
  | 'trophy'
  /** The first time anything happens: first log, first scan, first week. */
  | 'firsttime'
  /** The bed under a performance. Loops. */
  | 'ceremony'
  /** One slide advancing to the next. */
  | 'slide'
  /** The last slide of a performance resolving. */
  | 'signoff'
  /** An easter egg found. Quieter than it wants to be. */
  | 'secret'
  /** A big day, on the first open of it. */
  | 'bigday'

export interface CueDef {
  /** File under `public/sounds/`, without extension. */
  file: string
  /** Playback gain, 0–1, before the master trim. */
  gain: number
  /** Loops until stopped. Only the ceremony bed does. */
  loop?: boolean
}

export const CUES: Readonly<Record<CueName, CueDef>> = {
  greet: { file: 'greet', gain: 0.5 },
  reply: { file: 'reply', gain: 0.4 },
  notable: { file: 'notable', gain: 0.45 },
  trophy: { file: 'trophy', gain: 0.7 },
  firsttime: { file: 'firsttime', gain: 0.6 },
  ceremony: { file: 'ceremony', gain: 0.32, loop: true },
  slide: { file: 'slide', gain: 0.34 },
  signoff: { file: 'signoff', gain: 0.62 },
  secret: { file: 'secret', gain: 0.5 },
  bigday: { file: 'bigday', gain: 0.55 },
}

/**
 * How a mood bends a sound. Detune is a playback rate, so it moves pitch and
 * length together the way a tape does — which is exactly the artefact wanted
 * here: her gloomy register should sound like the same voice slowed down, not
 * like a different sample.
 */
export interface ToneShaping {
  rate: number
  gain: number
}

export const TONE_SHAPING: Readonly<Record<PresenceTone, ToneShaping>> = {
  plain: { rate: 1, gain: 1 },
  proud: { rate: 1.04, gain: 1.05 },
  gloomy: { rate: 0.94, gain: 0.82 },
  festive: { rate: 1.06, gain: 1.05 },
  neglected: { rate: 0.97, gain: 0.72 },
  loyal: { rate: 0.98, gain: 0.88 },
  astri: { rate: 1.06, gain: 1 },
}

/** Master trim. Everything above is relative to this; this is "mixed quiet". */
export const MASTER_GAIN = 0.13

/**
 * Where the ceremony bed actually loops.
 *
 * Not the ends of the file, on purpose. Every AAC encoder pads the start and
 * end of a stream with priming samples, and browsers hand those to
 * `decodeAudioData` along with the audio — so a buffer looped end-to-end
 * replays the padding every time round, which is a tick every few seconds
 * under a ceremony. The asset holds two periods of the drone and this loops
 * the middle one: both ends are clear of the padding, and they are exactly one
 * period apart, so the join is phase-continuous. See `generate-sounds.mjs`.
 */
export const LOOP = { start: 4, end: 12 } as const
